import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { RoleManagerService } from './role-manager.service';
import { firstValueFrom } from 'rxjs';

// Configurar las fuentes
(pdfMake as any).vfs = pdfFonts;

export interface PdfColumn {
  header: string;
  field: string;
  width?: string | number; // 'auto', '*', number
  align?: 'left' | 'center' | 'right';
  format?: (value: any) => string; // Función para formatear el valor
  bold?: boolean; // Si el texto de la columna debe ir en negrita
}

export interface PdfExportConfig {
  title: string;
  subtitle?: string;
  filename: string;
  orientation?: 'portrait' | 'landscape';
  columns: PdfColumn[];
  data: any[];
  pageSize?: 'A4' | 'A3' | 'LETTER';
  metadata?: {
    label: string;
    value: string;
  }[];
  footer?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PdfExportService {

  constructor(
    private roleManagerService: RoleManagerService,
    private http: HttpClient
  ) { }

  /**
   * Genera y descarga un PDF con los datos proporcionados
   */
  async exportToPdf(config: PdfExportConfig): Promise<void> {
    const docDefinition = await this.createDocDefinition(config);
    const pdf = pdfMake.createPdf(docDefinition);
    pdf.download(config.filename);
  }

  /**
   * Abre el PDF en una nueva pestaña
   */
  async openPdfInNewTab(config: PdfExportConfig): Promise<void> {
    const docDefinition = await this.createDocDefinition(config);
    const pdf = pdfMake.createPdf(docDefinition);
    pdf.open();
  }

  /**
   * Obtiene el logo correcto según el origin y lo convierte a base64
   */
  private async getLogoBase64(): Promise<string> {
    let logoPath: string;

    if (this.roleManagerService.isQuantum()) {
      logoPath = 'assets/images/logo_Quantum-Global_color_text.jpg';
    } else if (this.roleManagerService.isPharma()) {
      logoPath = 'assets/images/logo_Quantum-Pharma_High-color-text.png';
    } else if (this.roleManagerService.isHospitalPharma()) {
      logoPath = 'assets/images/logo_Quantum-Clinic_High-color-text.png';
    } else {
      logoPath = 'assets/images/logo_Quantum-Global_color_text.jpg'; // Default
    }

    try {
      const blob = await firstValueFrom(this.http.get(logoPath, { responseType: 'blob' }));
      return await this.blobToBase64(blob);
    } catch (error) {
      console.error('Error loading logo:', error);
      return '';
    }
  }

  /**
   * Obtiene el color de la cabecera según el origin
   */
  private getHeaderColor(): string {
    if (this.roleManagerService.isQuantum()) {
      return '#00549c';
    } else if (this.roleManagerService.isPharma()) {
      return '#008173';
    } else if (this.roleManagerService.isHospitalPharma()) {
      return '#d15e00';
    } else {
      return '#00549c'; // Default
    }
  }

  /**
   * Convierte un Blob a base64
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Crea la definición del documento PDF
   */
  private async createDocDefinition(config: PdfExportConfig): Promise<any> {
    const content: any[] = [];

    // Obtener logo y color de cabecera
    const logoBase64 = await this.getLogoBase64();
    const headerColor = this.getHeaderColor();

    // Header con logo y título
    if (logoBase64) {
      // Construir el stack de la derecha
      const rightStack: any[] = [
        {
          text: config.title,
          style: 'header',
          alignment: 'right'
        }
      ];

      if (config.subtitle) {
        rightStack.push({
          text: config.subtitle,
          style: 'subheader',
          alignment: 'right',
          margin: [0, 5, 0, 0]
        });
      }

      // Agregar metadatos debajo del título/subtítulo
      if (config.metadata && config.metadata.length > 0) {
        const metadataContent = config.metadata.map(item => ({
          text: [
            { text: `${item.label}: `, bold: true },
            { text: item.value }
          ],
          alignment: 'right',
          margin: [0, 2, 0, 0],
          fontSize: 9
        }));

        rightStack.push({
          stack: metadataContent,
          margin: [0, 10, 0, 0]
        });
      }

      content.push({
        columns: [
          {
            image: 'logo',
            width: 120,
            margin: [0, 0, 20, 0]
          },
          {
            stack: rightStack,
            width: '*'
          }
        ],
        margin: [0, 0, 0, 20]
      });
    } else {
      // Fallback si no hay logo
      content.push({
        text: config.title,
        style: 'header',
        alignment: 'center',
        margin: [0, 0, 0, 10]
      });

      if (config.subtitle) {
        content.push({
          text: config.subtitle,
          style: 'subheader',
          alignment: 'center',
          margin: [0, 0, 0, 10]
        });
      }

      // Metadata (información adicional como fechas, filtros, etc.)
      if (config.metadata && config.metadata.length > 0) {
        const metadataContent = config.metadata.map(item => ({
          text: [
            { text: `${item.label}: `, bold: true },
            { text: item.value }
          ],
          margin: [0, 2, 0, 2]
        }));

        content.push({
          stack: metadataContent,
          margin: [0, 0, 0, 15]
        });
      }
    }

    // Tabla
    const tableBody: any[] = [];

    // Headers
    const headers = config.columns.map(col => ({
      text: col.header,
      style: 'tableHeader',
      alignment: col.align || 'left'
    }));
    tableBody.push(headers);

    // Datos
    config.data.forEach(row => {
      const dataRow = config.columns.map(col => {
        let value = row[col.field];

        // Aplicar formato si existe
        if (col.format) {
          value = col.format(value);
        } else if (value === null || value === undefined) {
          value = '-';
        }

        const cell: any = {
          text: value.toString(),
          alignment: col.align || 'left'
        };

        // Aplicar negrita si está configurado
        if (col.bold) {
          cell.bold = true;
        }

        return cell;
      });
      tableBody.push(dataRow);
    });

    // Configurar anchos de columna
    const widths = config.columns.map(col => col.width || 'auto');

    content.push({
      table: {
        headerRows: 1,
        widths: widths,
        body: tableBody
      },
      layout: {
        fillColor: (rowIndex: number) => {
          return rowIndex === 0 ? headerColor : (rowIndex % 2 === 0 ? '#F5F5F5' : null);
        },
        hLineWidth: (i: number, node: any) => 0.5,
        vLineWidth: (i: number, node: any) => 0,
        hLineColor: (i: number, node: any) => '#CCCCCC',
        vLineColor: (i: number, node: any) => '#CCCCCC',
      }
    });

    // Definición del documento
    const docDefinition: any = {
      pageSize: config.pageSize || 'A4',
      pageOrientation: config.orientation || 'landscape',
      pageMargins: [30, 30, 30, 30],
      content: content,
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          color: '#333333'
        },
        subheader: {
          fontSize: 14,
          color: '#666666'
        },
        tableHeader: {
          bold: true,
          fontSize: 10,
          color: 'white',
          fillColor: headerColor
        }
      },
      defaultStyle: {
        fontSize: 9
      },
      footer: (currentPage: number, pageCount: number) => {
        const footerContent: any[] = [
          {
            text: `Página ${currentPage} de ${pageCount}`,
            alignment: 'center',
            fontSize: 8,
            color: '#666666'
          }
        ];

        if (config.footer) {
          footerContent.unshift({
            text: config.footer,
            alignment: 'center',
            fontSize: 8,
            color: '#666666',
            margin: [0, 0, 0, 5]
          });
        }

        return {
          stack: footerContent,
          margin: [40, 10, 40, 0]
        };
      }
    };

    // Agregar logo si existe
    if (logoBase64) {
      docDefinition.images = {
        logo: logoBase64
      };
    }

    return docDefinition;
  }
}
